<template>
  <div class="email-routing-page animated fadeIn">
    <b-card class="panel-card">
      <div slot="header" class="panel-header">
        <span><i class="fa fa-random"></i> Routing rules</span>
        <span class="email-muted">Updated {{ formatDate(routingRulesPayload.updated_at) }}</span>
      </div>

      <div class="routing-default-row">
        <div class="routing-default-copy">
          <strong>Default route</strong>
          <span>Used when no domain or MX rule matches.</span>
        </div>
        <div class="routing-default-control">
          <select v-model="routingDefaultRoute" class="form-control form-control-sm" :disabled="!canEdit" @change="saveDefaultRoute">
            <option v-for="option in routeOptions" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
          <span v-if="!canEdit" class="email-muted">View only</span>
        </div>
      </div>

      <div v-if="canEdit" class="routing-rule-editor">
        <select v-model="newRouteRule.match" class="form-control form-control-sm">
          <option v-for="option in routeMatchOptions" :key="option.value" :value="option.value">
            {{ option.label }}
          </option>
        </select>
        <input
          v-model.trim="newRouteRule.pattern"
          class="form-control form-control-sm"
          type="text"
          placeholder="hotmail.com"
          @keyup.enter="addRoutingRule"
        >
        <select v-model="newRouteRule.route" class="form-control form-control-sm">
          <option v-for="option in routeOptions" :key="option.value" :value="option.value">
            {{ option.label }}
          </option>
        </select>
        <input
          v-model.trim="newRouteRule.note"
          class="form-control form-control-sm"
          type="text"
          placeholder="Note"
          @keyup.enter="addRoutingRule"
        >
        <b-button size="sm" variant="primary" :disabled="!canAddRoutingRule" @click="addRoutingRule">
          <i class="fa fa-plus"></i>
        </b-button>
      </div>

      <div class="routing-table-shell">
        <div class="table-responsive route-table-wrap">
          <table class="table table-sm table-hover email-routing-table mb-0">
            <thead>
              <tr>
                <th>Match</th>
                <th>Pattern</th>
                <th>Route</th>
                <th>Note</th>
                <th v-if="canEdit"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="rule in routingRules" :key="routeRuleKey(rule)">
                <td>{{ routeMatchLabel(rule.match) }}</td>
                <td>{{ rule.pattern }}</td>
                <td>
                  <b-badge :variant="routeVariant(rule.route)">{{ sourceLabel(rule.route) }}</b-badge>
                </td>
                <td>{{ rule.note || '-' }}</td>
                <td v-if="canEdit" class="text-right">
                  <b-button size="sm" variant="outline-danger" class="route-delete-button" @click="deleteRoutingRule(rule)">
                    <i class="fa fa-trash"></i>
                  </b-button>
                </td>
              </tr>
              <tr v-if="routingRules.length === 0">
                <td :colspan="canEdit ? 5 : 4" class="email-empty">No routing rules configured.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </b-card>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import Permissions from '../../_permissions'

export default {
  name: 'EmailRouting',
  data () {
    return {
      routingDefaultRoute: 'namecheap',
      newRouteRule: {
        match: 'domain',
        pattern: '',
        route: 'vps',
        note: ''
      },
      routeMatchOptions: [
        { value: 'domain', label: 'Domain' },
        { value: 'mx_contains', label: 'MX contains' }
      ],
      routeOptions: [
        { value: 'vps', label: 'VPS' },
        { value: 'namecheap', label: 'Namecheap' }
      ]
    }
  },
  computed: {
    ...mapGetters({
      authUser: 'authUser',
      storeRoutingRules: 'EmailRoutingRules'
    }),
    routingRulesPayload () {
      return Object.assign({ default: 'namecheap', rules: [], updated_at: null }, this.storeRoutingRules || {})
    },
    routingDefaultFromStore () {
      return this.routingRulesPayload.default || 'namecheap'
    },
    routingRules () {
      return this.routingRulesPayload.rules || []
    },
    canEdit () {
      return this.hasPermission('EmailRoutingEdit')
    },
    canAddRoutingRule () {
      return Boolean(this.canEdit && this.newRouteRule.match && this.newRouteRule.pattern && this.newRouteRule.route)
    }
  },
  watch: {
    routingDefaultFromStore: {
      immediate: true,
      handler (value) {
        this.routingDefaultRoute = value || 'namecheap'
      }
    },
    '$route.query': {
      immediate: true,
      deep: true,
      handler () {
        this.applyRouteQuery()
      }
    }
  },
  mounted () {
    this.loadRoutingRules()
  },
  methods: {
    loadRoutingRules () {
      this.emit('getEmailRoutingRules', {})
    },
    saveRoutingRules (rules) {
      if (!this.canEdit) return
      this.emit('saveEmailRoutingRules', { rules, default: this.routingDefaultRoute })
    },
    saveDefaultRoute () {
      this.saveRoutingRules(this.routingRules)
    },
    applyRouteQuery () {
      const query = this.$route.query || {}
      const pattern = this.queryValue(query.pattern)
      const match = this.queryValue(query.match) || 'domain'
      const route = this.queryValue(query.route) || 'vps'
      const note = this.queryValue(query.note)
      if (!pattern && !query.match && !query.route && !note) return
      this.newRouteRule.match = this.validRouteMatch(match) ? match : 'domain'
      this.newRouteRule.pattern = String(pattern || '').trim()
      this.newRouteRule.route = this.validRouteTarget(route) ? route : 'vps'
      this.newRouteRule.note = String(note || '').trim()
    },
    queryValue (value) {
      if (Array.isArray(value)) return value[0]
      return value
    },
    validRouteMatch (match) {
      return this.routeMatchOptions.some(option => option.value === match)
    },
    validRouteTarget (route) {
      return this.routeOptions.some(option => option.value === route)
    },
    addRoutingRule () {
      if (!this.canAddRoutingRule) return
      const rule = {
        match: this.newRouteRule.match,
        pattern: this.newRouteRule.pattern,
        route: this.newRouteRule.route,
        note: this.newRouteRule.note
      }
      this.saveRoutingRules(this.routingRules.concat([rule]))
      this.newRouteRule.pattern = ''
      this.newRouteRule.note = ''
    },
    deleteRoutingRule (rule) {
      const key = this.routeRuleKey(rule)
      this.saveRoutingRules(this.routingRules.filter(current => this.routeRuleKey(current) !== key))
    },
    routeRuleKey (rule) {
      return rule.id || `${rule.match || ''}-${rule.pattern || ''}-${rule.route || ''}`
    },
    routeMatchLabel (match) {
      const option = this.routeMatchOptions.find(option => option.value === match)
      return option ? option.label : (match || '-')
    },
    routeVariant (route) {
      return route === 'vps' ? 'info' : 'primary'
    },
    sourceLabel (source) {
      const value = String(source || '').trim()
      if (!value || value === 'unknown') return '-'
      const labels = {
        vps: 'VPS',
        namecheap: 'Namecheap'
      }
      return labels[value.toLowerCase()] || value
    },
    formatDate (value) {
      if (!value) return '-'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return '-'
      return date.toLocaleString()
    },
    hasPermission (name) {
      const permission = this.findPermission(name)
      const user = this.authUser
      if (!user || !permission) return false
      if (permission.and(user.deny).neq(0)) return false
      if (permission.and(user.allow).neq(0)) return true
      return permission.and(user.permission).neq(0)
    },
    findPermission (name) {
      for (let category in Permissions) {
        for (let permission in Permissions[category].Permissions) {
          if (permission === name) {
            return Permissions[category].Permissions[permission].Permission
          }
        }
      }
      return null
    }
  }
}
</script>

<style scoped>
.email-routing-page {
  color: #e4e7ea;
}

.email-muted {
  color: #a4abb3;
  font-size: 0.82rem;
}

.panel-card {
  margin-bottom: 16px;
}

.panel-header {
  align-items: center;
  display: flex;
  justify-content: space-between;
}

.routing-default-row {
  align-items: center;
  border-bottom: 1px solid #3a4149;
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  justify-content: space-between;
  padding: 10px 12px;
}

.routing-default-copy {
  display: grid;
  gap: 2px;
}

.routing-default-copy strong {
  color: #f0f3f5;
  font-size: 0.9rem;
}

.routing-default-copy span {
  color: #a4abb3;
  font-size: 0.82rem;
}

.routing-default-control {
  align-items: center;
  display: flex;
  gap: 10px;
}

.routing-default-row .form-control {
  background: #2f353a;
  border-color: #3a4149;
  color: #e4e7ea;
  width: 132px;
}

.routing-rule-editor {
  border-bottom: 1px solid #3a4149;
  display: grid;
  gap: 8px;
  grid-template-columns: 132px minmax(170px, 1fr) 132px minmax(150px, 1fr) 38px;
  padding: 10px 12px 12px;
}

.routing-rule-editor .form-control {
  background: #2f353a;
  border-color: #3a4149;
  color: #e4e7ea;
}

.route-table-wrap {
  max-height: 420px;
  overflow-y: auto;
}

.routing-table-shell {
  padding: 12px;
}

.email-routing-table {
  color: #e4e7ea;
}

.email-routing-table th,
.email-routing-table td {
  border-color: #3a4149;
  color: #e4e7ea;
  vertical-align: middle;
}

.email-routing-table thead th {
  background: #2f353a;
  color: #f0f3f5;
  font-size: 0.74rem;
  letter-spacing: 0;
  position: sticky;
  text-transform: uppercase;
  top: 0;
  white-space: nowrap;
  z-index: 2;
}

.email-routing-table tbody tr:hover td {
  background: #343b41;
  color: #fff;
}

.route-delete-button {
  line-height: 1;
  min-width: 30px;
}

.email-empty {
  color: #a4abb3 !important;
  padding: 18px 8px !important;
  text-align: center;
}

@media (max-width: 1199px) {
  .routing-rule-editor {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 767px) {
  .routing-rule-editor {
    grid-template-columns: 1fr;
  }
}
</style>
